// packages/utils/src/masks.ts

/**
 * Applies CPF mask to a string.
 * @example maskCPF("12345678909") -> "123.456.789-09"
 */
export function maskCPF(value: string): string {
  if (value.includes('*')) return value;
  const cleaned = value.replace(/[^\d]/g, '').slice(0, 11);
  return cleaned
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

/**
 * Applies CNPJ mask to a string.
 * @example maskCNPJ("11222333000181") -> "11.222.333/0001-81"
 */
export function maskCNPJ(value: string): string {
  if (value.includes('*')) return value;
  const cleaned = value.replace(/[^\d]/g, '').slice(0, 14);
  return cleaned
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

/**
 * Applies phone mask (Brazilian format with DDD).
 * @example maskPhone("11912345678") -> "(11) 91234-5678"
 * @example maskPhone("1134567890") -> "(11) 3456-7890"
 */
export function maskPhone(value: string): string {
  if (value.includes('*')) return value;
  const cleaned = value.replace(/[^\d]/g, '').slice(0, 11);

  if (cleaned.length <= 2) return cleaned.replace(/^(\d{0,2})/, '($1');
  if (cleaned.length <= 6) return cleaned.replace(/^(\d{2})(\d{0,4})/, '($1) $2');
  if (cleaned.length <= 10) {
    return cleaned.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  }
  return cleaned.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
}

/**
 * Masks a phone number for privacy DISPLAY, revealing only the last 4 digits.
 * The full number stays intact for tel:/wa.me links elsewhere — this helper is
 * for on-screen display only. Bullets mimic the Brazilian layout when there is
 * a DDD (10–11 digits); shorter numbers fall back to a plain "•••• NNNN".
 * Returns null for null/empty or fewer-than-4-digit inputs.
 * @example maskPhoneLast4("11912345678") -> "(••) •••••-5678"
 * @example maskPhoneLast4("1134567890") -> "(••) ••••-7890"
 * @example maskPhoneLast4("1234") -> "•••• 1234"
 */
export function maskPhoneLast4(value: string | null | undefined): string | null {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length < 4) return null;
  const last4 = digits.slice(-4);
  if (digits.length >= 10) {
    const middle = '•'.repeat(digits.length - 6); // total − DDD(2) − last4(4)
    return `(••) ${middle}-${last4}`;
  }
  return `•••• ${last4}`;
}

/**
 * Applies Brazilian ZIP code (CEP) mask.
 * @example maskCEP("12345678") -> "12345-678"
 * @example maskCEP("12345") -> "12345"
 */
export function maskCEP(value: string): string {
  const cleaned = value.replace(/\D/g, '').slice(0, 8);
  if (cleaned.length <= 5) return cleaned;
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
}

/**
 * Clean digit-only mask for a RENAVAM (no separators; 11 digits).
 * @example maskRenavam("0144.615.4421") -> "01446154421"
 */
export function maskRenavam(value: string): string {
  return value.replace(/\D/g, '').slice(0, 11);
}

/**
 * Normalizes a RENAVAM to its canonical 11-digit form, left-padding with zeros.
 * Historic RENAVAMs are shorter (9 digits) and users frequently omit leading
 * zeros (e.g. "1446154421" for "01446154421"), so any 9–11 digit input is
 * accepted and padded. Returns `null` when there are fewer than 9 or more than
 * 11 digits (unrecoverable).
 * @example normalizeRenavam("1446154421") -> "01446154421"
 * @example normalizeRenavam("01446154421") -> "01446154421"
 * @example normalizeRenavam("123") -> null
 */
export function normalizeRenavam(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 9 || digits.length > 11) return null;
  return digits.padStart(11, '0');
}

/**
 * Applies vehicle plate mask.
 * @example maskPlate("ABC1234") -> "ABC-1234"
 * @example maskPlate("ABC1D23") -> "ABC1D23" (Mercosul, no hyphen)
 */
export function maskPlate(value: string): string {
  const cleaned = value
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 7);

  // Detect Mercosul format (4th char is digit, 5th is letter). charAt returns
  // "" past the end, so the regex tests short-circuit safely.
  if (cleaned.length >= 5 && /^\d$/.test(cleaned.charAt(3)) && /^[A-Z]$/.test(cleaned.charAt(4))) {
    return cleaned; // Mercosul format: no hyphen
  }

  // Old format: insert hyphen after 3 letters
  if (cleaned.length > 3) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }

  return cleaned;
}

/**
 * Applies BRL currency mask for input fields.
 * @example maskCurrency("150050") -> "1.500,50"
 * @example maskCurrency("100") -> "1,00"
 */
export function maskCurrency(value: string): string {
  const cleaned = value.replace(/[^\d]/g, '');
  if (!cleaned) return '';

  const numberValue = parseInt(cleaned, 10) / 100;
  return numberValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Applies a Brazilian thousands separator to an integer value (no decimals).
 * Strips every non-digit and groups by 3 with a dot, so "80000" renders as
 * "80.000". Use for odometer/quantity fields where the value is a whole
 * number but the display needs a thousands separator. Empty / digit-less
 * input returns "".
 * @example maskThousands("80000") -> "80.000"
 * @example maskThousands(45000) -> "45.000"
 * @example maskThousands("1.234") -> "1.234"
 */
export function maskThousands(value: string | number): string {
  const cleaned = String(value).replace(/\D/g, '');
  if (!cleaned) return '';
  return Number(cleaned).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

/**
 * Strips every non-digit from a thousands-masked string and returns the
 * clean digit string ("" when there are no digits). Pair with
 * `maskThousands` on display and `Number(parseThousands(x))` to recover the
 * numeric value on submit.
 * @example parseThousands("80.000") -> "80000"
 * @example parseThousands("R$ 1.234") -> "1234"
 */
export function parseThousands(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Applies Brazilian date mask progressively while the user types.
 * Strips non-digits, caps at 8 digits, and inserts the slashes at the
 * right positions so typing "12052026" renders as "12/05/2026".
 * @example maskDateBR("1") -> "1"
 * @example maskDateBR("1205") -> "12/05"
 * @example maskDateBR("12052026") -> "12/05/2026"
 * @example maskDateBR("12/05/2026") -> "12/05/2026"
 */
export function maskDateBR(value: string): string {
  const cleaned = value.replace(/\D/g, '').slice(0, 8);
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
}

/**
 * Converts a Brazilian "DD/MM/YYYY" string to ISO "YYYY-MM-DD". Returns
 * `undefined` for empty input, malformed strings, or invalid dates (e.g.
 * "31/02/2026"). Use this on the way out to the API.
 */
export function dateBRToISO(value: string): string | undefined {
  if (!value) return undefined;
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;
  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  // Reject impossible months/days fast and verify via Date roundtrip so
  // "31/02" is rejected.
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return undefined;
  }
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Converts an ISO "YYYY-MM-DD" string (or a date-only prefix of an ISO
 * timestamp) to Brazilian "DD/MM/YYYY". Returns "" for empty / invalid
 * inputs so it is safe to assign to a controlled input value.
 */
export function isoToDateBR(value: string | null | undefined): string {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Today's date in the user's LOCAL timezone as ISO "YYYY-MM-DD". Avoids
 * `toISOString()`, which converts to UTC and can shift the day near midnight
 * (e.g. 22h BRT already reads as tomorrow in UTC).
 */
export function todayISO(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}
